/**
 * Free Gift Manager
 *
 * Automatically manages free gift promotions based on cart contents.
 * Uses metaobject-based promo configuration for centralised control.
 *
 */

class FreeGiftManager {
  constructor() {
    this.debug = true; // Set to false in production
    this.isProcessing = false;
    this.promoDataCache = new Map();
    this.promoData = [];
    this.giftPropertyKey = '_is_free_gift';
    this.promoIdPropertyKey = '_promo_id';

    this.init();
  }

  init() {
    this.log('Initializing Free Gift Manager...');
    this.loadPromoData();
    this.setupEventListeners();
    this.evaluateCart();
    this.log('Free Gift Manager initialized');
  }

  log(...args) {
    if (this.debug) {
      console.log('[FreeGiftManager]', ...args);
    }
  }

  error(...args) {
    console.error('[FreeGiftManager]', ...args);
  }

  loadPromoData() {
    const promoDataElement = document.getElementById('free-gift-promo-data');

    if (!promoDataElement) {
      this.log('No promo data element found on initial load. Will fetch dynamically on cart updates.');
      this.promoData = [];
      this.promoDataCache.clear();
      return;
    }

    this.parseAndCachePromoData(promoDataElement.textContent);
  }

  parseAndCachePromoData(jsonString) {
    this.promoDataCache.clear();

    try {
      this.promoData = JSON.parse(jsonString);
      this.log('Loaded promo data:', this.promoData);

      // Cache product-specific promos by trigger product ID for quick lookups.
      // Promos with no trigger_product_ids are universal and handled in identifyTriggerProducts.
      this.promoData.forEach(promo => {
        const triggerId = promo.trigger_product_ids;
        if (!triggerId || triggerId === '') return;

        const pid = triggerId.toString();
        if (!this.promoDataCache.has(pid)) {
          this.promoDataCache.set(pid, []);
        }
        this.promoDataCache.get(pid).push(promo);
      });

      this.log('Promo data cache:', Array.from(this.promoDataCache.entries()));
    } catch (e) {
      this.error('Failed to parse promo data:', e);
      this.promoData = [];
    }
  }

  async fetchFreshPromoData() {
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const promoDataElement = doc.getElementById('free-gift-promo-data');

      const debugComments = html.match(/<!--\s*\[FreeGiftPromoData DEBUG\][^>]*-->/g);
      if (debugComments) {
        this.log('Section debug output:', debugComments);
      }

      if (promoDataElement) {
        this.log('Raw promo data JSON from section:', promoDataElement.textContent.trim());
        this.parseAndCachePromoData(promoDataElement.textContent);

        const domElement = document.getElementById('free-gift-promo-data');
        if (domElement) {
          domElement.textContent = promoDataElement.textContent;
        }
      } else {
        this.log('No promo data element found in fresh section response');
        this.log('Section response excerpt:', html.substring(0, 500));
        this.promoData = [];
        this.promoDataCache.clear();
      }
    } catch (e) {
      this.error('Failed to fetch fresh promo data:', e);
    }
  }

  setupEventListeners() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'free-gift-manager') {
          this.log('Skipping cart update from self');
          return;
        }
        this.log('Cart update detected:', event);
        this.evaluateCart();
      });
      this.log('Subscribed to PUB_SUB_EVENTS.cartUpdate');
    } else {
      this.log('PUB_SUB not available, using fallback polling');
      this.startPolling();
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.log('Page became visible, re-evaluating cart');
        this.evaluateCart();
      }
    });
  }

  startPolling() {
    this.lastCartToken = null;

    setInterval(async () => {
      try {
        const cart = await this.fetchCart();
        if (cart.token !== this.lastCartToken) {
          this.lastCartToken = cart.token;
          this.evaluateCartWithData(cart);
        }
      } catch (e) {
        this.error('Polling error:', e);
      }
    }, 2000);
  }

  async fetchCart() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      this.error('Failed to fetch cart:', e);
      throw e;
    }
  }

  async evaluateCart() {
    if (this.isProcessing) {
      this.log('Already processing, skipping evaluation');
      return;
    }

    try {
      await this.fetchFreshPromoData();
      const cart = await this.fetchCart();
      await this.evaluateCartWithData(cart);
    } catch (e) {
      this.error('Failed to evaluate cart:', e);
    }
  }

  async evaluateCartWithData(cart) {
    if (this.isProcessing) {
      this.log('Already processing, skipping evaluation');
      return;
    }

    this.isProcessing = true;
    this.log('Evaluating cart:', cart);

    try {
      const currentGifts = this.identifyGiftsInCart(cart);
      this.log('Current gifts in cart:', currentGifts);

      const triggerProducts = this.identifyTriggerProducts(cart);
      this.log('Trigger products in cart:', triggerProducts);

      const requiredGifts = this.calculateRequiredGifts(triggerProducts);
      this.log('Required gifts:', requiredGifts);

      const giftsToRemove = this.getGiftsToRemove(currentGifts, requiredGifts);
      this.log('Gifts to remove:', giftsToRemove);

      const giftsToAdd = this.getGiftsToAdd(currentGifts, requiredGifts);
      this.log('Gifts to add:', giftsToAdd);

      if (giftsToRemove.length > 0) {
        await this.removeGiftsFromCart(giftsToRemove);
      }

      if (giftsToAdd.length > 0) {
        await this.addGiftsToCart(giftsToAdd);
      }

      await this.syncGiftQuantities(cart, requiredGifts);

    } catch (e) {
      this.error('Error during cart evaluation:', e);
    } finally {
      this.isProcessing = false;
    }
  }

  isPromoActive(promo) {
    if (promo.enabled !== true && promo.enabled !== 'true') {
      this.log(`Promo "${promo.name}" is disabled (enabled=${JSON.stringify(promo.enabled)})`);
      return false;
    }

    const now = new Date();

    if (promo.start_date && promo.start_date !== '') {
      const startDate = new Date(promo.start_date + 'T00:00:00');
      if (now < startDate) {
        this.log(`Promo "${promo.name}" hasn't started yet (starts ${promo.start_date})`);
        return false;
      }
    }

    if (promo.end_date && promo.end_date !== '') {
      const endDate = new Date(promo.end_date + 'T23:59:59');
      if (now > endDate) {
        this.log(`Promo "${promo.name}" has expired (ended ${promo.end_date})`);
        return false;
      }
    }

    return true;
  }

  identifyGiftsInCart(cart) {
    return cart.items.filter(item => {
      return item.properties && item.properties[this.giftPropertyKey] === 'true';
    }).map(item => ({
      key: item.key,
      variantId: item.variant_id,
      quantity: item.quantity,
      promoId: item.properties ? item.properties[this.promoIdPropertyKey] : null,
      lineIndex: cart.items.indexOf(item) + 1
    }));
  }

  /**
   * Identify trigger products in the cart that qualify for gifts.
   * Loops all promos directly — universal promos (no trigger_product_ids) fire when
   * any non-gift items meet the min quantity; product-specific promos fire when the
   * matching product is in the cart.
   */
  identifyTriggerProducts(cart) {
    const triggers = [];

    let totalNonGiftQuantity = 0;
    cart.items.forEach(item => {
      if (item.properties && item.properties[this.giftPropertyKey] === 'true') return;
      totalNonGiftQuantity += item.quantity;
    });

    this.log('Total non-gift quantity:', totalNonGiftQuantity);
    this.log('Promo data:', this.promoData);

    this.promoData.forEach(promo => {
      if (!this.isPromoActive(promo)) return;

      const minQty = promo.min_trigger_quantity || 1;
      const triggerId = promo.trigger_product_ids;

      if (!triggerId || triggerId === '') {
        // Universal promo — trigger if enough non-gift items in cart
        if (totalNonGiftQuantity >= minQty) {
          this.log(`Universal promo "${promo.name}" triggered (cart qty: ${totalNonGiftQuantity})`);
          triggers.push({
            productId: 'global',
            variantId: null,
            quantity: totalNonGiftQuantity,
            promo: promo,
            lineKey: null
          });
        }
      } else {
        // Product-specific promo — find the matching cart item
        cart.items.forEach(item => {
          if (item.properties && item.properties[this.giftPropertyKey] === 'true') return;

          if (item.product_id.toString() === triggerId.toString()) {
            if (item.quantity >= minQty) {
              this.log(`Product promo "${promo.name}" triggered by "${item.title}"`);
              triggers.push({
                productId: item.product_id.toString(),
                variantId: item.variant_id,
                quantity: item.quantity,
                promo: promo,
                lineKey: item.key
              });
            }
          }
        });
      }
    });

    return triggers;
  }

  calculateRequiredGifts(triggerProducts) {
    const requiredGifts = new Map();

    triggerProducts.forEach(trigger => {
      const promo = trigger.promo;
      const giftVariantId = promo.gift_variant_id;
      const promoId = promo.id;

      if (!giftVariantId || giftVariantId === '' || giftVariantId === 'null') {
        this.log(`Promo "${promo.name}" has no gift_variant_id configured (value: ${JSON.stringify(giftVariantId)})`);
        return;
      }

      let giftQuantity = promo.gift_quantity || 1;

      if (promo.quantity_match) {
        const minQty = promo.min_trigger_quantity || 1;
        giftQuantity = Math.floor(trigger.quantity / minQty) * giftQuantity;
      }

      const key = `${giftVariantId}-${promoId}`;
      if (requiredGifts.has(key)) {
        const existing = requiredGifts.get(key);
        existing.quantity = Math.max(existing.quantity, giftQuantity);
      } else {
        requiredGifts.set(key, {
          variantId: giftVariantId,
          quantity: giftQuantity,
          promoId: promoId,
          promoName: promo.name
        });
      }
    });

    return Array.from(requiredGifts.values());
  }

  getGiftsToRemove(currentGifts, requiredGifts) {
    return currentGifts.filter(currentGift => {
      const stillRequired = requiredGifts.some(required =>
        required.variantId.toString() === currentGift.variantId.toString() &&
        required.promoId === currentGift.promoId
      );
      return !stillRequired;
    });
  }

  getGiftsToAdd(currentGifts, requiredGifts) {
    return requiredGifts.filter(required => {
      const alreadyInCart = currentGifts.some(current =>
        current.variantId.toString() === required.variantId.toString() &&
        current.promoId === required.promoId
      );
      return !alreadyInCart;
    });
  }

  async removeGiftsFromCart(giftsToRemove) {
    this.log('Removing gifts from cart:', giftsToRemove);

    for (const gift of giftsToRemove) {
      try {
        await this.updateCartItem(gift.key, 0);
        this.log(`Removed gift variant ${gift.variantId} from cart`);
        this.showGiftRemovedNotification(gift);
      } catch (e) {
        this.error(`Failed to remove gift variant ${gift.variantId}:`, e);
      }
    }

    this.publishCartUpdate();
  }

  async addGiftsToCart(giftsToAdd) {
    this.log('Adding gifts to cart:', giftsToAdd);

    for (const gift of giftsToAdd) {
      try {
        await this.addItemToCart(gift.variantId, gift.quantity, {
          [this.giftPropertyKey]: 'true',
          [this.promoIdPropertyKey]: gift.promoId
        });
        this.log(`Added gift variant ${gift.variantId} to cart (qty: ${gift.quantity})`);
        this.showGiftAddedNotification(gift);
      } catch (e) {
        this.error(`Failed to add gift variant ${gift.variantId}:`, e);
        if (e.message && e.message.includes('not available')) {
          this.showGiftUnavailableNotification(gift);
        }
      }
    }

    this.publishCartUpdate();
  }

  async syncGiftQuantities(cart, requiredGifts) {
    const currentGifts = this.identifyGiftsInCart(cart);

    for (const required of requiredGifts) {
      const currentGift = currentGifts.find(g =>
        g.variantId.toString() === required.variantId.toString() &&
        g.promoId === required.promoId
      );

      if (currentGift && currentGift.quantity !== required.quantity) {
        this.log(`Updating gift quantity from ${currentGift.quantity} to ${required.quantity}`);
        try {
          await this.updateCartItem(currentGift.key, required.quantity);
        } catch (e) {
          this.error('Failed to sync gift quantity:', e);
        }
      }
    }
  }

  async addItemToCart(variantId, quantity, properties = {}) {
    const body = JSON.stringify({
      id: variantId,
      quantity: quantity,
      properties: properties
    });

    const response = await fetch(routes.cart_add_url, {
      ...this.fetchConfig(),
      body: body
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add item to cart');
    }

    return await response.json();
  }

  async updateCartItem(lineKey, quantity) {
    const body = JSON.stringify({
      id: lineKey,
      quantity: quantity
    });

    const response = await fetch(routes.cart_change_url, {
      ...this.fetchConfig(),
      body: body
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to update cart item');
    }

    return await response.json();
  }

  fetchConfig() {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  }

  publishCartUpdate() {
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-manager' });
    }
  }

  showGiftAddedNotification(gift) {
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        type: 'success',
        message: `🎁 Free gift added to your cart!`
      });
    } else {
      this.log('Gift added notification:', gift.promoName);
    }
  }

  showGiftRemovedNotification(gift) {
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        type: 'info',
        message: 'A promotional gift has been removed as the offer has ended.'
      });
    } else {
      this.log('Gift removed notification');
    }
  }

  showGiftUnavailableNotification(gift) {
    if (typeof window.showNotification === 'function') {
      window.showNotification({
        type: 'warning',
        message: 'Sorry, the free gift is currently out of stock.'
      });
    } else {
      this.log('Gift unavailable notification');
    }
  }

  async removeAllGifts() {
    this.log('Removing all gifts from cart');
    const cart = await this.fetchCart();
    const gifts = this.identifyGiftsInCart(cart);
    await this.removeGiftsFromCart(gifts);
  }

  async refreshPromoData() {
    await this.fetchFreshPromoData();
    this.evaluateCart();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('free-gift-promo-data') ||
      window.location.pathname.includes('/cart') ||
      document.querySelector('cart-drawer')) {
    window.freeGiftManager = new FreeGiftManager();
  }
});

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    if (!window.freeGiftManager &&
        (document.getElementById('free-gift-promo-data') ||
         window.location.pathname.includes('/cart') ||
         document.querySelector('cart-drawer'))) {
      window.freeGiftManager = new FreeGiftManager();
    }
  }, 100);
}